import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, EffectFade, Autoplay, Keyboard, Pagination } from "swiper/modules";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import {
  ArrowRight, Play, Plus, Minus, Quote, Star,
  Award, Globe, ShieldCheck, Factory, Package, Palette, Tag, Layers, FileCheck, Search, Clock,
  Hand, Sparkles, Leaf, FlaskConical,
  MessageSquare, FileText, Scissors, Truck,
  MapPin,
} from "lucide-react";

function WhatsAppIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}
import {
  TRUST_INDICATORS, USPS_8, PRODUCT_HIGHLIGHTS, ORDER_PROCESS,
  PRODUCT_CATEGORIES, PROCESS_STEPS, PROJECTS, TESTIMONIALS, GOOGLE_REVIEWS,
  FAQS, COUNTRIES, COUNTER_STATS, SITE, WHATSAPP_LINK,
  HERO_SLIDES, COMPANY_VIDEO_URL, CUSTOMER_VIDEOS,
} from "../data/site";
import LeadForm from "../components/LeadForm";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/effect-fade";
import "swiper/css/pagination";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

/* Country pin coordinates (lon, lat) for Global Presence map */
const COUNTRY_PINS = [
  { name: "USA",          coords: [-98.35, 39.50] },
  { name: "Canada",       coords: [-106.34, 56.13] },
  { name: "Argentina",    coords: [-63.62, -38.42] },
  { name: "Brazil",       coords: [-55.50, -10.00] },
  { name: "France",       coords: [2.21, 46.23] },
  { name: "Italy",        coords: [12.57, 41.87] },
  { name: "Germany",      coords: [10.45, 51.17] },
  { name: "Portugal",     coords: [-8.22, 39.40] },
  { name: "Netherlands",  coords: [5.29, 52.13] },
  { name: "New Zealand",  coords: [172.89, -40.90] },
  { name: "China",        coords: [104.20, 35.86] },
  { name: "Japan",        coords: [138.25, 36.20] },
  { name: "Singapore",    coords: [103.82, 1.35] },
  { name: "Australia",    coords: [133.77, -25.27] },
  { name: "Sweden",       coords: [18.64, 60.13] },
  { name: "Hungary",      coords: [19.50, 47.16] },
  { name: "Spain",        coords: [-3.75, 40.46] },
  { name: "UK",           coords: [-3.44, 55.38] },
  { name: "Ireland",      coords: [-8.24, 53.41] },
  { name: "South Africa", coords: [22.94, -30.56] },
  { name: "Russia",       coords: [105.32, 61.52] },
];

const ICON_MAP = {
  award: Award, globe: Globe, shield: ShieldCheck, factory: Factory,
  package: Package, palette: Palette, tag: Tag, layers: Layers, "file-check": FileCheck, search: Search, clock: Clock,
  hand: Hand, sparkles: Sparkles, leaf: Leaf, flask: FlaskConical,
  "message-square": MessageSquare, "file-text": FileText, scissors: Scissors, truck: Truck, "shield-check": ShieldCheck,
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

/* ============== HERO — 3-slide fade carousel, ~70vh ============== */
function Hero() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 80]);
  return (
    <section data-testid="hero-section" className="relative h-[70vh] min-h-[500px] w-full overflow-hidden bg-[#1e2a5a]">
      {/* Background carousel */}
      <motion.div className="absolute inset-0" style={{ y: heroY }}>
        <Swiper
          modules={[EffectFade, Autoplay, Pagination]}
          effect="fade"
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          loop
          speed={1200}
          pagination={{ clickable: true, el: ".hero-pagination" }}
          className="w-full h-full"
        >
          {HERO_SLIDES.map((slide, i) => (
            <SwiperSlide key={i}>
              <img src={slide.img} alt={slide.alt} className="w-full h-full object-cover" />
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="absolute inset-0 bg-gradient-to-b from-[#1e2a5a]/60 via-[#1e2a5a]/40 to-[#1a1a1a]/80 z-10 pointer-events-none" />
      </motion.div>

      {/* Content overlay */}
      <div className="relative z-20 h-full max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 flex flex-col justify-center pt-20 pb-16">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="max-w-3xl">
          <div className="inline-flex items-center gap-2 glass-dark rounded-full px-4 py-2 mb-8 text-[#faf6ef] text-xs font-bold tracking-wider uppercase">
            <span className="w-2 h-2 rounded-full bg-[#d98f2f] animate-pulse" />
            Since 2007 · Jaipur, India
          </div>
          <h1 className="font-serif text-[#faf6ef] text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-light mb-8">
            Luxury handmade textiles, rooted in four centuries of <em className="italic text-[#d98f2f]">block print</em> tradition.
          </h1>
          <p className="text-[#faf6ef]/85 text-base md:text-lg max-w-xl leading-[1.8] mb-10">
            Curated collections of hand block-printed cotton and linen — crafted by master artisans in Jaipur, for homes and brands that value provenance.
          </p>
          <a
            href="#categories"
            data-testid="hero-primary-cta"
            className="btn-pill bg-[#d98f2f] text-[#1a1a1a] hover:bg-[#faf6ef] hover:text-[#1e2a5a] shadow-cta py-5 px-8 text-sm md:text-base font-bold"
          >
            Explore Our Collections
            <ArrowRight size={18} strokeWidth={2.2} />
          </a>
        </motion.div>
      </div>

      {/* Pagination dots */}
      <div className="hero-pagination absolute bottom-6 left-0 right-0 z-30 flex justify-center gap-2" />
    </section>
  );
}

/* ---- Count-up hook: animates 0→target over `duration`ms, restarts when `active` flips true ---- */
function useCountUp(target, active, duration = 3200) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    cancelAnimationFrame(raf.current);
    if (!active) { setVal(0); return; }
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setVal(Math.round(eased * target));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [active, target, duration]);
  return val;
}

/* Parses "18+" → { num: 18, suffix: "+" }, "100%" → { num: 100, suffix: "%" }, "IEC" → null */
function parseNumeric(big) {
  const m = big.match(/^(\d+)([+%]?)$/);
  if (!m) return null;
  return { num: parseInt(m[1], 10), suffix: m[2] };
}

function TrustTile({ t, i, isActive, onHover, isLast }) {
  const Icon = ICON_MAP[t.icon] || Award;
  const parsed = parseNumeric(t.big);
  const count = useCountUp(parsed ? parsed.num : 0, isActive && !!parsed);
  // Last tile: icon + number always use accent green regardless of active state
  const valueColor = isLast ? t.accent : (isActive ? t.accent : "#1e2a5a");

  return (
    <div
      data-testid={`trust-${i}`}
      className="relative p-5 md:p-7 group cursor-default overflow-hidden"
      onMouseEnter={() => onHover(i)}
    >
      {/* progress line */}
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#1a1a1a]/5">
        <div
          key={isActive ? `a-${i}` : `i-${i}`}
          className={`h-full ${isActive ? "counter-line" : ""}`}
          style={{ background: t.accent }}
        />
      </div>
      <div className="flex items-center gap-3 mb-3 mt-1">
        <Icon size={20} strokeWidth={1.8} className="transition-colors" style={{ color: valueColor }} />
        <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#1a1a1a]/55">{t.sub}</span>
      </div>
      {/* Animated number — calendar-flip feel via key-driven re-mount */}
      <div className="text-3xl md:text-4xl font-extrabold leading-none mb-2 transition-colors tabular-nums" style={{ color: valueColor }}>
        {parsed ? (
          <span key={isActive ? "on" : "off"} style={{ display: "inline-block", animation: isActive ? "flipIn 0.35s ease-out" : "none" }}>
            {isActive ? count : parsed.num}{parsed.suffix}
          </span>
        ) : t.big}
      </div>
      <div className="text-sm md:text-base text-[#1a1a1a]/80 font-semibold leading-snug">{t.title}</div>
    </div>
  );
}

/* ============== TRUST INDICATORS — narrative heritage strip with stats ============== */
function TrustRotator() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive((i) => (i + 1) % TRUST_INDICATORS.length), 3500);
    return () => clearInterval(t);
  }, []);
  return (
    <section data-testid="trust-rotator" className="relative py-20 md:py-28 bg-cream-gradient border-y border-[#1a1a1a]/8 overflow-hidden">
      <style>{`
        @keyframes flipIn {
          0%   { transform: rotateX(-90deg) scaleY(0.4); opacity: 0; }
          60%  { transform: rotateX(8deg)  scaleY(1.05); opacity: 1; }
          100% { transform: rotateX(0deg)  scaleY(1);    opacity: 1; }
        }
      `}</style>
      {/* Subtle handmade paper texture overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 40% 60%, #1a1a1a 0.5px, transparent 0.5px)", backgroundSize: "20px 20px" }} />
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        {/* Narrative heading */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          className="text-center mb-12 md:mb-16"
        >
          <div className="text-[#d98f2f] text-xs tracking-[0.3em] uppercase font-bold mb-4">Our Heritage</div>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight font-light max-w-3xl mx-auto mb-6">
            A legacy of craft, <em className="italic text-[#b4462a]">trusted worldwide</em>.
          </h2>
          <p className="text-[#1a1a1a]/65 text-base md:text-lg max-w-2xl mx-auto leading-[1.8]">
            For over 18 years, our workshop in Jaipur has served discerning buyers across 30+ countries — with the same commitment to hand-crafted quality and in-house accountability.
          </p>
        </motion.div>

        <div className="rounded-[2rem] bg-white shadow-soft overflow-hidden">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[#1a1a1a]/8">
            {TRUST_INDICATORS.map((t, i) => (
              <TrustTile
                key={t.title}
                t={t} i={i}
                isActive={active === i}
                isLast={i === TRUST_INDICATORS.length - 1}
                onHover={setActive}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============== AUTO-PLAY VIDEO — plays in viewport, pauses on scroll ============== */
function AutoPlayVideo() {
  const videoRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    const section = sectionRef.current;
    if (!video || !section) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const videoUrl = COMPANY_VIDEO_URL;
  const posterImg = "https://images.unsplash.com/photo-1606722590583-6951b5ea92ad?w=1600&q=80&fit=crop";

  return (
    <section ref={sectionRef} data-testid="autoplay-video" className="py-20 md:py-28 bg-[#faf6ef]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
        >
          <div className="border-gold-gradient p-[1.5px] rounded-[2rem]">
            <div className="rounded-[2rem] overflow-hidden bg-[#1e2a5a] aspect-video relative">
              {videoUrl ? (
                <video
                  ref={videoRef}
                  src={videoUrl}
                  poster={posterImg}
                  muted
                  playsInline
                  loop
                  preload="metadata"
                  className="w-full h-full object-cover"
                />
              ) : (
                /* Poster placeholder when no video URL is set */
                <div className="relative w-full h-full">
                  <img src={posterImg} alt="Indus Art workshop" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-b from-[#1e2a5a]/20 to-[#1e2a5a]/50 flex items-center justify-center">
                    <div className="glass-dark w-20 h-20 rounded-full flex items-center justify-center">
                      <Play size={32} strokeWidth={1.5} className="text-[#faf6ef] ml-1" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ============== CUSTOMER LOVE — video testimonial library ============== */
function CustomerLove() {
  return (
    <section data-testid="customer-love" className="py-20 md:py-28 bg-cream-gradient overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          className="text-center mb-12 md:mb-16"
        >
          <div className="text-[#d98f2f] text-xs tracking-[0.3em] uppercase font-bold mb-4">Customer Love</div>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight font-light max-w-3xl mx-auto">
            Stories from those who <em className="italic text-[#b4462a]">wear our craft</em>.
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {CUSTOMER_VIDEOS.map((v, i) => (
            <motion.div
              key={i}
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-40px" }}
              variants={fadeUp} transition={{ delay: i * 0.08 }}
              className="group cursor-pointer"
            >
              <div className="border-gold-gradient p-[1px] rounded-2xl">
                <div className="relative aspect-[9/16] overflow-hidden rounded-2xl bg-[#1e2a5a]">
                  <img
                    src={v.thumbnail}
                    alt={v.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1a1a1a]/70" />
                  {/* Play overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="glass-dark w-14 h-14 rounded-full flex items-center justify-center opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all">
                      <Play size={22} strokeWidth={1.5} className="text-[#faf6ef] ml-0.5" />
                    </div>
                  </div>
                  {/* Name overlay */}
                  {v.name && v.name !== "Coming Soon" && (
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="font-semibold text-sm text-[#faf6ef]">{v.name}</div>
                      {v.location && <div className="text-xs text-[#faf6ef]/70">{v.location}</div>}
                    </div>
                  )}
                  {v.name === "Coming Soon" && (
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                      <div className="text-xs text-[#faf6ef]/50 font-semibold uppercase tracking-wider">Coming Soon</div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============== 8 USPs (AUTO-HIGHLIGHT) ============== */
function WhyIndusArt() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive((i) => (i + 1) % USPS_8.length), 3000);
    return () => clearInterval(t);
  }, []);
  return (
    <section data-testid="why-section" className="py-24 md:py-32 bg-[#faf6ef] relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#d98f2f]/10 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#1e2a5a]/10 blur-3xl" />

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="text-center mb-14 md:mb-20">
          <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-4">Why Indus Art</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-light max-w-3xl mx-auto">
            Eight quiet reasons <em className="italic text-[#b4462a]">buyers choose us</em>.
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {USPS_8.map((u, i) => {
            const Icon = ICON_MAP[u.icon] || Package;
            const isActive = active === i;
            return (
              <motion.button
                key={u.title}
                onClick={() => setActive(i)}
                data-testid={`usp-${i}`}
                animate={{ scale: isActive ? 1.05 : 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className={`relative rounded-3xl p-5 md:p-7 text-left transition-all duration-500 ${
                  isActive ? "bg-white shadow-soft z-10" : "bg-white/60 shadow-sm hover:bg-white/80"
                }`}
              >
                <div
                  className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center mb-4 transition-all ${
                    isActive ? "bg-madder-gradient text-[#faf6ef] pulse-ring" : "bg-[#1e2a5a]/8 text-[#1e2a5a]"
                  }`}
                >
                  <Icon size={22} strokeWidth={1.8} />
                </div>
                <div className="font-serif text-lg md:text-xl text-[#1a1a1a] font-semibold leading-snug mb-2">{u.title}</div>
                <div className="text-xs md:text-sm text-[#1a1a1a]/65 leading-[1.6]">{u.sub}</div>
                {isActive && (
                  <motion.div layoutId="usp-dot" className="absolute top-4 right-4 w-2 h-2 rounded-full bg-[#d98f2f]" />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============== PRODUCTS + HIGHLIGHTS ============== */
function ProductsCatalog() {
  const [catFilter, setCatFilter] = useState("");
  const filtered = catFilter
    ? PRODUCT_CATEGORIES.filter((c) => c.name.toLowerCase().includes(catFilter.toLowerCase()))
    : PRODUCT_CATEGORIES;

  return (
    <section id="categories" data-testid="products-section" className="py-24 md:py-32 bg-indigo-gradient text-[#faf6ef] overflow-hidden relative">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #faf6ef 1px, transparent 1px)", backgroundSize: "32px 32px" }} />

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 mb-12 grid md:grid-cols-12 gap-8 items-end">
        <div className="md:col-span-8">
          <div className="text-[#d98f2f] text-xs tracking-[0.3em] uppercase font-bold mb-4">Our Collections</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">A curated home collection, crafted to travel.</h2>
        </div>
        <div className="md:col-span-4">
          {/* Category filter */}
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#faf6ef]/50" />
            <input
              type="text"
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              placeholder="Find a category..."
              className="w-full pl-10 pr-10 py-3 rounded-full bg-white/10 border border-[#faf6ef]/15 text-[#faf6ef] placeholder:text-[#faf6ef]/40 text-sm focus:border-[#d98f2f] focus:outline-none focus:ring-2 focus:ring-[#d98f2f]/20 transition-all"
            />
            {catFilter && (
              <button onClick={() => setCatFilter("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#faf6ef]/50 hover:text-[#faf6ef] text-xs font-bold">
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="relative products-coverflow px-2 md:px-0">
        <Swiper
          key={catFilter}
          effect="coverflow"
          grabCursor centeredSlides loop={filtered.length > 3}
          keyboard={{ enabled: true }}
          slidesPerView="auto"
          autoplay={catFilter ? false : { delay: 5000, pauseOnMouseEnter: true, disableOnInteraction: false }}
          coverflowEffect={{ rotate: 28, stretch: 0, depth: 220, modifier: 1, slideShadows: false }}
          pagination={{ clickable: true }}
          modules={[EffectCoverflow, Autoplay, Keyboard, Pagination]}
        >
          {filtered.map((c) => (
            <SwiperSlide key={c.key} style={{ width: 340 }} className="!h-auto" data-testid={`category-slide-${c.key}`}>
              <Link to={`/products/${c.slug}`} className="block group">
                {/* Premium saffron gradient border */}
                <div className="border-gold-gradient p-[1.5px] rounded-[2rem]">
                  <div className="relative aspect-[3/4] overflow-hidden rounded-[2rem] bg-white/5 shadow-soft">
                    <img src={c.img} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1a1a1a]/80" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="font-serif text-2xl md:text-3xl">{c.name}</div>
                      <div className="mt-2 text-xs tracking-[0.2em] uppercase text-[#d98f2f] font-bold inline-flex items-center gap-1">
                        Explore <ArrowRight size={12} strokeWidth={2.2} />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>
        {catFilter && filtered.length === 0 && (
          <div className="text-center py-12 text-[#faf6ef]/60 text-sm">No categories match "{catFilter}"</div>
        )}
      </div>

      <div className="text-center mt-12 md:mt-16">
        <Link to="/products" data-testid="browse-catalogue-btn" className="btn-pill bg-[#d98f2f] text-[#1a1a1a] hover:bg-[#faf6ef] hover:text-[#1e2a5a] shadow-cta py-4 px-8 text-sm">
          Explore the full collection <ArrowRight size={16} strokeWidth={2.2} />
        </Link>
      </div>

      {/* Product highlights — 4 pillars common across categories with animated icons */}
      <div className="relative mt-20 md:mt-24 max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {PRODUCT_HIGHLIGHTS.map((p, i) => {
            const Icon = ICON_MAP[p.icon] || Hand;
            return (
              <motion.div
                key={p.title}
                initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp} transition={{ delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                className="rounded-3xl glass-dark p-5 md:p-7 group cursor-default" data-testid={`product-highlight-${i}`}
              >
                <motion.div
                  className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-[#d98f2f]/20 text-[#d98f2f] flex items-center justify-center mb-4 relative overflow-hidden"
                  whileHover={{ rotate: [0, -10, 10, -6, 0], scale: 1.1, backgroundColor: "rgba(217,143,47,0.35)" }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                  <motion.div
                    animate={{ y: [0, -3, 0] }}
                    transition={{ duration: 2.4 + i * 0.2, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
                  >
                    <Icon size={22} strokeWidth={1.8} />
                  </motion.div>
                  <motion.span
                    className="absolute inset-0 rounded-2xl border-2 border-[#d98f2f]/0"
                    animate={{ borderColor: ["rgba(217,143,47,0)", "rgba(217,143,47,0.5)", "rgba(217,143,47,0)"] }}
                    transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
                  />
                </motion.div>
                <div className="text-lg md:text-xl font-extrabold mb-2">{p.title}</div>
                <div className="text-xs md:text-sm text-[#faf6ef]/75 leading-[1.6]">{p.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============== CRAFT STORY ============== */
function CraftStory() {
  return (
    <section data-testid="craft-section" className="py-24 md:py-32 bg-[#faf6ef]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-12 gap-12 items-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="md:col-span-6">
          <div className="text-[#8a9a7b] text-xs tracking-[0.3em] uppercase font-bold mb-5">The Craft</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light text-[#1e2a5a] mb-8">
            Four centuries of <em className="italic text-[#b4462a]">block print</em>, pressed into cotton.
          </h2>
          <p className="drop-cap text-[#1a1a1a]/85 text-base md:text-lg leading-[1.9] mb-6">
            Bagru and Sanganer, the twin villages outside Jaipur, have carried the block-print tradition for over 400 years. Teak is hand-carved into blocks — a single repeat can take weeks. Indigo is fermented in clay vats. Madder red is boiled from root.
          </p>
          <Link to="/our-craft" data-testid="craft-learn-more" className="btn-pill bg-[#1e2a5a] text-[#faf6ef] hover:bg-[#b4462a] shadow-pill py-3.5 px-7 text-sm">
            Learn more about the craft <ArrowRight size={15} strokeWidth={2.2} />
          </Link>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="md:col-span-6 relative">
          <div className="relative aspect-[4/5] overflow-hidden rounded-[2.5rem] shadow-soft">
            <img src="https://images.pexels.com/photos/7037689/pexels-photo-7037689.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720" alt="Artisan block printing" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-[#1e2a5a]/10" />
            <button data-testid="craft-video-play" className="absolute inset-0 flex items-center justify-center group" aria-label="Play craft video">
              <span className="w-20 h-20 md:w-24 md:h-24 rounded-full glass flex items-center justify-center group-hover:scale-110 transition shadow-soft">
                <Play size={26} strokeWidth={1.8} className="text-[#1e2a5a] ml-1" />
              </span>
            </button>
          </div>
          <div className="absolute -bottom-6 -left-6 hidden md:block w-48 h-64 overflow-hidden rounded-[2rem] border-[6px] border-[#faf6ef] shadow-soft">
            <img src="https://images.pexels.com/photos/6694716/pexels-photo-6694716.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=640&w=440" alt="Block carving" className="w-full h-full object-cover" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ============== ORDER PROCESS (vertical timeline, "You are here" on step 1) ============== */
function OrderProcessSection() {
  return (
    <section data-testid="order-process" className="py-24 md:py-32 bg-white">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <div className="text-center mb-14 md:mb-20">
          <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-4">Order Process</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light max-w-3xl mx-auto">
            Just <em className="text-[#b4462a] font-extrabold not-italic">one step</em> to get the best products and service — <span className="italic">we'll handle the rest.</span>
          </h2>
          <p className="mt-6 text-[#1a1a1a]/70 leading-[1.8] max-w-xl mx-auto">Six transparent stages from your first message to the carton landing at your warehouse.</p>
        </div>

        <div className="relative pl-5 md:pl-0">
          {/* vertical line */}
          <div className="absolute left-9 md:left-1/2 top-4 bottom-4 w-[2px] bg-gradient-to-b from-[#d98f2f] via-[#1e2a5a]/30 to-[#1e2a5a]/10 md:-translate-x-px" />

          <div className="space-y-6 md:space-y-10">
            {ORDER_PROCESS.map((s, i) => {
              const Icon = ICON_MAP[s.icon] || MessageSquare;
              const isFirst = i === 0;
              const isLeft = i % 2 === 0;

              return (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`order-step-${i}`}
                  className={`relative flex md:grid md:grid-cols-2 md:gap-12 items-start ${
                    isLeft ? "" : "md:[&>*:first-child]:col-start-2"
                  }`}
                >
                  {/* content card */}
                  <div className={`flex-1 pl-16 md:pl-0 ${isLeft ? "md:text-right md:pr-10" : "md:pl-10"}`}>
                    <div className={`inline-block rounded-3xl p-5 md:p-6 shadow-sm hover:shadow-soft transition-all text-left ${
                      isFirst ? "bg-cream-gradient ring-2 ring-[#d98f2f]/40" : "bg-cream-gradient"
                    }`}>
                      {isFirst && (
                        <motion.div
                          animate={{ scale: [1, 1.05, 1] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                          className="inline-flex items-center gap-1.5 bg-[#d98f2f] text-[#1a1a1a] rounded-full px-3 py-1 text-[10px] font-extrabold tracking-[0.15em] uppercase mb-3"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[#1a1a1a] animate-pulse" />
                          You are here
                        </motion.div>
                      )}
                      <div className="flex items-center gap-3 mb-2">
                        <div className="text-2xl text-[#d98f2f] font-extrabold">{s.n}</div>
                        <div className="text-lg md:text-xl text-[#1e2a5a] font-extrabold leading-snug">{s.title}</div>
                      </div>
                      <div className="text-sm text-[#1a1a1a]/70 leading-[1.7]">{s.d}</div>
                      {isFirst && (
                        <Link
                          to="/contact#lead-form"
                          data-testid="order-step-cta"
                          className="mt-4 inline-flex items-center gap-1.5 text-xs font-extrabold text-[#b4462a] hover:text-[#1e2a5a] transition"
                        >
                          Start now <ArrowRight size={14} strokeWidth={2.4} />
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* center icon node */}
                  <motion.div
                    whileInView={{ scale: [0.5, 1.15, 1] }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.08 + 0.1 }}
                    className={`absolute left-0 md:left-1/2 md:-translate-x-1/2 top-2 w-[4.5rem] h-[4.5rem] md:w-20 md:h-20 rounded-full flex items-center justify-center z-10 shadow-pill ${
                      isFirst
                        ? "bg-madder-gradient text-[#faf6ef] pulse-ring"
                        : "bg-white text-[#1e2a5a] border-2 border-[#1e2a5a]/15"
                    }`}
                  >
                    <Icon size={24} strokeWidth={1.8} />
                  </motion.div>

                  {/* spacer for alternate side on desktop */}
                  <div className="hidden md:block" />
                </motion.div>
              );
            })}
          </div>
        </div>

        <div className="mt-14 md:mt-16 text-center">
          <Link to="/contact#lead-form" data-testid="order-process-cta" className="btn-pill bg-[#1e2a5a] text-[#faf6ef] hover:bg-[#b4462a] shadow-cta py-4 px-8 text-sm">
            Take your first step <ArrowRight size={16} strokeWidth={2.2} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ============== QUOTE FORM ============== */
function QuoteFormSection() {
  return (
    <section id="lead-form" data-testid="quote-form-section" className="py-24 md:py-32 bg-[#faf6ef] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[40vw] h-[40vw] rounded-full bg-[#d98f2f]/8 blur-3xl" />

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-5">
          <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-4">Request a Quote</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light mb-6">
            Share a brief. <em className="italic text-[#b4462a]">Receive a quote within 24 hours.</em>
          </h2>
          <p className="text-[#1a1a1a]/75 leading-[1.8] mb-8 text-base md:text-lg">
            Five fields. One specialist. A personalised quote with pricing, lead time, and recommended design path.
          </p>
          <div className="flex items-center gap-3 text-sm text-[#1a1a1a]/70 rounded-2xl bg-white/60 p-4 shadow-sm">
            <WhatsAppIcon size={18} />
            Prefer chat?{" "}
            <a href={WHATSAPP_LINK()} target="_blank" rel="noreferrer" onClick={() => window.gtag?.("event", "whatsapp_click", { location: "quote_form_section" })} className="inline-flex items-center gap-1 font-bold text-[#25D366] hover:text-[#20c05c] transition">
              <WhatsAppIcon size={14} /> WhatsApp us
            </a>
          </div>
        </div>
        <div className="md:col-span-7">
          <LeadForm source="home_quote_form" />
        </div>
      </div>
    </section>
  );
}

/* ============== GLOBAL PRESENCE ============== */
function GlobalPresence() {
  const highlighted = new Set(COUNTRIES.map((c) => c.iso));
  return (
    <section data-testid="global-presence" className="py-24 md:py-32 bg-white">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
          <div className="md:col-span-7">
            <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-4">Global Presence</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
              Exporting from Jaipur to <em className="italic text-[#b4462a]">30+ countries</em>.
            </h2>
          </div>
          <div className="md:col-span-5 text-[#1a1a1a]/70 leading-[1.8]">Every marked country has received an Indus Art carton.</div>
        </div>

        <div className="rounded-[2.5rem] overflow-hidden bg-cream-gradient shadow-soft aspect-[16/9] relative">
          <ComposableMap projectionConfig={{ scale: 160 }} style={{ width: "100%", height: "100%" }}>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const iso = geo.properties?.["ISO_A3"] || geo.id;
                  const isHighlighted = highlighted.has(iso) || highlighted.has(geo.properties?.name);
                  return (
                    <Geography key={geo.rsmKey} geography={geo}
                      style={{
                        default: { fill: isHighlighted ? "#1e2a5a" : "#e9e3d5", stroke: "#faf6ef", strokeWidth: 0.5, outline: "none" },
                        hover:   { fill: isHighlighted ? "#b4462a" : "#d6cfbf", outline: "none" },
                        pressed: { fill: "#b4462a", outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
            {COUNTRY_PINS.map((p, i) => (
              <Marker key={p.name} coordinates={p.coords} data-testid={`map-pin-${i}`}>
                <g className="map-pin">
                  <circle r={8} fill="#d98f2f" fillOpacity={0.25}>
                    <animate attributeName="r" values="6;14;6" dur="2.4s" repeatCount="indefinite" begin={`${(i * 0.12).toFixed(2)}s`} />
                    <animate attributeName="fill-opacity" values="0.35;0;0.35" dur="2.4s" repeatCount="indefinite" begin={`${(i * 0.12).toFixed(2)}s`} />
                  </circle>
                  <circle r={3.5} fill="#b4462a" stroke="#faf6ef" strokeWidth={1} />
                  <title>{p.name}</title>
                </g>
              </Marker>
            ))}
          </ComposableMap>
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {COUNTER_STATS.map((s, i) => (
            <div key={s.l} className="rounded-3xl bg-cream-gradient p-6 md:p-8 text-center shadow-sm" data-testid={`counter-${i}`}>
              <div className="font-serif text-4xl md:text-5xl text-[#1e2a5a] font-semibold">{s.n}</div>
              <div className="text-xs tracking-[0.2em] uppercase text-[#1a1a1a]/60 mt-3 font-bold">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============== TESTIMONIALS ============== */
function TestimonialsSection() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(t);
  }, []);
  return (
    <section data-testid="testimonials-section" className="py-24 md:py-32 bg-[#faf6ef]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-5">
          <div className="text-[#8a9a7b] text-xs tracking-[0.3em] uppercase font-bold mb-4">From Our Buyers</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light mb-6">
            What wholesale teams <em className="italic text-[#b4462a]">say, unprompted.</em>
          </h2>
          <p className="text-[#1a1a1a]/70 leading-[1.8]">Direct quotes from sourcing teams we've worked with.</p>
        </div>

        <div className="md:col-span-7 relative">
          <div className="rounded-[2.5rem] bg-white shadow-soft p-8 md:p-12">
            <Quote size={48} strokeWidth={1.2} className="text-[#d98f2f] mb-6" />
            <AnimatePresence mode="wait">
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.5 }} data-testid={`testimonial-${i}`}>
                <blockquote className="font-serif text-xl md:text-2xl lg:text-3xl leading-[1.45] text-[#1a1a1a] mb-6">
                  "{TESTIMONIALS[i].q}"
                </blockquote>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#1e2a5a] text-[#faf6ef] flex items-center justify-center font-serif text-xl">
                    {TESTIMONIALS[i].n.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#1e2a5a]">{TESTIMONIALS[i].n}</div>
                    <div className="text-xs tracking-wider text-[#1a1a1a]/60 mt-0.5">{TESTIMONIALS[i].c}</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="mt-6 flex gap-2 justify-center">
            {TESTIMONIALS.map((_, n) => (
              <button key={n} onClick={() => setI(n)} data-testid={`testimonial-dot-${n}`} aria-label={`Testimonial ${n + 1}`}
                className={`h-2 rounded-full transition-all ${i === n ? "w-8 bg-[#1e2a5a]" : "w-2 bg-[#1a1a1a]/20 hover:bg-[#1a1a1a]/40"}`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============== GOOGLE REVIEWS ============== */
function GoogleReviewsSection() {
  return (
    <section data-testid="google-reviews-section" className="py-24 md:py-32 bg-cream-gradient">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-4 flex items-center gap-2">
              <GoogleG /> Google Reviews
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">Verified, five-star, unprompted.</h2>
          </div>
          <a href="https://google.com/search?q=indus+art+jaipur+reviews" target="_blank" rel="noreferrer" data-testid="google-reviews-link"
             className="btn-pill bg-white text-[#1e2a5a] hover:bg-[#1e2a5a] hover:text-[#faf6ef] shadow-pill py-3 px-5 text-xs border border-[#1e2a5a]/10 self-start">
             Read all reviews on Google <ArrowRight size={14} strokeWidth={2.2} />
          </a>
        </div>

        {/* Aggregate card */}
        <div className="rounded-3xl bg-white shadow-soft p-6 md:p-8 mb-8 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
          <div className="flex-shrink-0">
            <div className="font-serif text-6xl md:text-7xl text-[#1e2a5a] font-semibold leading-none">{GOOGLE_REVIEWS.aggregate}</div>
            <div className="flex gap-1 mt-2">
              {[...Array(5)].map((_, n) => <Star key={n} size={18} strokeWidth={1.5} className="fill-[#d98f2f] text-[#d98f2f]" />)}
            </div>
            <div className="text-xs text-[#1a1a1a]/60 mt-2">{GOOGLE_REVIEWS.total} reviews</div>
          </div>
          <div className="flex-1 border-t md:border-t-0 md:border-l border-[#1a1a1a]/10 pt-5 md:pt-0 md:pl-10">
            <div className="text-sm text-[#1a1a1a]/70 leading-[1.8]">
              Indus Art is rated <span className="font-bold text-[#1e2a5a]">{GOOGLE_REVIEWS.aggregate} / 5</span> across {GOOGLE_REVIEWS.total} Google reviews from wholesale partners and visiting buyers. Below are recent five-star reviews — verified by Google.
            </div>
            <p className="text-[10px] text-[#1a1a1a]/40 mt-3 italic">* Placeholder reviews. Live Google Places API pull will replace once the API key is configured.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {GOOGLE_REVIEWS.reviews.map((r, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-3xl bg-white p-6 shadow-sm hover:shadow-soft transition-all" data-testid={`gr-${i}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#d98f2f]/20 text-[#d98f2f] flex items-center justify-center font-serif text-lg font-bold">{r.avatar}</div>
                <div>
                  <div className="text-sm font-bold text-[#1e2a5a]">{r.author}</div>
                  <div className="text-[10px] text-[#1a1a1a]/50 tracking-wide">{r.when}</div>
                </div>
              </div>
              <div className="flex gap-0.5 mb-3">
                {[...Array(r.rating)].map((_, n) => <Star key={n} size={13} strokeWidth={1.5} className="fill-[#d98f2f] text-[#d98f2f]" />)}
              </div>
              <div className="text-sm text-[#1a1a1a]/75 leading-[1.6]">{r.text}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GoogleG() {
  return (
    <span className="inline-flex items-center gap-1">
      <svg width="14" height="14" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
        <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"/>
        <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
        <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
      </svg>
    </span>
  );
}

/* ============== END-TO-END MANUFACTURING (mobile-first accordion, all steps visible, first auto-expanded) ============== */
function ManufacturingStepper() {
  const [open, setOpen] = useState(0);
  return (
    <section id="manufacturing" data-testid="manufacturing-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-[1000px] mx-auto px-6 md:px-10">
        <div className="text-[#8a9a7b] text-xs tracking-[0.3em] uppercase font-bold mb-4">End-to-End Manufacturing</div>
        <h2 className="text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light mb-4">
          From cotton to carton — <em className="italic text-[#b4462a]">every step under one roof</em>.
        </h2>
        <p className="text-[#1a1a1a]/70 max-w-2xl leading-[1.8] mb-10 md:mb-14 text-base md:text-lg">
          Seven stages. Zero hand-offs to trading companies. Tap any step to expand.
        </p>

        <div className="space-y-3 md:space-y-4">
          {PROCESS_STEPS.map((s, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className={`rounded-3xl overflow-hidden transition-all ${isOpen ? "bg-white shadow-soft ring-1 ring-[#1e2a5a]/10" : "bg-cream-gradient shadow-sm"}`}
                data-testid={`process-step-${i}`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`process-step-toggle-${i}`}
                  className="w-full flex items-center gap-4 md:gap-6 p-5 md:p-6 text-left"
                >
                  <div className={`shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center font-extrabold transition-all ${
                    isOpen ? "bg-[#1e2a5a] text-[#faf6ef] shadow-pill" : "bg-white text-[#1e2a5a] border border-[#1a1a1a]/10"
                  }`}>
                    <span className="text-lg md:text-xl">{s.n}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-lg md:text-xl font-extrabold text-[#1e2a5a] leading-snug">{s.t}</div>
                    {!isOpen && <div className="text-xs md:text-sm text-[#1a1a1a]/55 mt-1 truncate">{s.d}</div>}
                  </div>
                  <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    isOpen ? "bg-[#d98f2f] text-[#1a1a1a] rotate-180" : "bg-white border border-[#1a1a1a]/10 text-[#1e2a5a]"
                  }`}>
                    {isOpen ? <Minus size={18} strokeWidth={2.4} /> : <Plus size={18} strokeWidth={2.4} />}
                  </div>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="grid md:grid-cols-5 gap-5 p-5 md:p-6 pt-0 md:pt-0">
                        <div className="md:col-span-2">
                          <div className="aspect-[4/3] overflow-hidden rounded-2xl">
                            <img src={s.img} alt={s.t} className="w-full h-full object-cover" loading="lazy" />
                          </div>
                        </div>
                        <div className="md:col-span-3">
                          <div className="text-[#d98f2f] text-xs tracking-[0.25em] uppercase font-extrabold mb-2">Step {s.n}</div>
                          <p className="text-[#1a1a1a]/80 leading-[1.9] text-base md:text-lg">{s.d}</p>
                          {i < PROCESS_STEPS.length - 1 && (
                            <button
                              onClick={() => setOpen(i + 1)}
                              className="mt-5 inline-flex items-center gap-1.5 text-xs font-extrabold text-[#b4462a] hover:text-[#1e2a5a] transition"
                            >
                              Next: {PROCESS_STEPS[i + 1].t} <ArrowRight size={14} strokeWidth={2.4} />
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============== ON FILM ============== */
function OnFilmSection() {
  const [active, setActive] = useState(0);
  const tabs = [
    { t: "The Making",        d: "Block print process, 60–90 seconds.", img: "https://images.pexels.com/photos/7037689/pexels-photo-7037689.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400" },
    { t: "Inside Our Unit",   d: "A walk through the Jaipur workshop.",  img: "https://images.unsplash.com/photo-1758269664127-1f744a56e06c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHx0ZXh0aWxlJTIwZmFjdG9yeSUyMHdvcmtzaG9wfGVufDB8fHx8MTc3NjU3NDYwMXww&ixlib=rb-4.1.0&q=85" },
    { t: "Meet the Artisans", d: "The people behind the press.",         img: "https://images.pexels.com/photos/6850854/pexels-photo-6850854.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400" },
  ];
  return (
    <section data-testid="on-film-section" className="py-24 md:py-32 bg-[#faf6ef]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-4">On Film</div>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light mb-10">See the hand before you see the invoice.</h2>

        <div className="flex flex-wrap gap-2 md:gap-3 mb-8">
          {tabs.map((t, i) => (
            <button key={t.t} onClick={() => setActive(i)} data-testid={`video-tab-${i}`}
              className={`rounded-full px-5 py-3 text-xs md:text-sm font-bold tracking-wide transition-all ${
                active === i ? "bg-[#1e2a5a] text-[#faf6ef] shadow-pill" : "bg-white text-[#1a1a1a]/80 hover:bg-[#1e2a5a]/5 border border-[#1a1a1a]/10"
              }`}>
              {t.t}
            </button>
          ))}
        </div>
        <div className="relative aspect-[16/9] w-full bg-[#1e2a5a] overflow-hidden rounded-[2rem] shadow-soft">
          <img src={tabs[active].img} alt={tabs[active].t} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1e2a5a]/80 via-[#1e2a5a]/20 to-transparent" />
          <button data-testid={`video-play-${active}`} className="absolute inset-0 flex items-center justify-center group" aria-label="Play">
            <span className="w-20 h-20 md:w-24 md:h-24 rounded-full glass flex items-center justify-center group-hover:scale-110 transition shadow-soft">
              <Play size={28} strokeWidth={1.8} className="text-[#1e2a5a] ml-1" />
            </span>
          </button>
          <div className="absolute bottom-6 left-6 text-[#faf6ef]">
            <div className="font-serif text-2xl md:text-3xl font-light">{tabs[active].t}</div>
            <div className="text-sm text-[#faf6ef]/80 mt-1">{tabs[active].d}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============== FAQ ============== */
function FAQSection() {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="faq-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-[1000px] mx-auto px-6 md:px-10">
        <div className="text-center mb-12 md:mb-16">
          <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-4">Questions buyers ask</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
            Straight answers, <em className="italic text-[#b4462a]">no small print</em>.
          </h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} data-testid={`faq-item-${i}`}
              className={`rounded-3xl overflow-hidden transition-all ${open === i ? "bg-white shadow-soft" : "bg-cream-gradient shadow-sm"}`}>
              <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-toggle-${i}`} className="w-full flex items-center justify-between p-6 md:p-7 text-left gap-4">
                <span className="font-serif text-lg md:text-xl text-[#1a1a1a] font-semibold pr-4">{f.q}</span>
                <span className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${open === i ? "bg-[#1e2a5a] text-[#faf6ef] rotate-180" : "bg-white text-[#1e2a5a]"}`}>
                  {open === i ? <Minus size={18} strokeWidth={2.2} /> : <Plus size={18} strokeWidth={2.2} />}
                </span>
              </button>
              <div className={`overflow-hidden transition-all duration-500 ${open === i ? "max-h-[500px]" : "max-h-0"}`}>
                <p className="px-6 md:px-7 pb-6 md:pb-7 text-[#1a1a1a]/75 leading-[1.9]">{f.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============== FINAL BAND ============== */
function FinalBand() {
  return (
    <section data-testid="final-band" className="py-24 md:py-36 bg-madder-gradient text-[#faf6ef] relative overflow-hidden rounded-t-[2.5rem]">
      <div className="absolute inset-0 opacity-10 pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #faf6ef 2px, transparent 2px)", backgroundSize: "40px 40px" }} />

      <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 text-center">
        <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-light mb-10">
          Let's make something remarkable together.
        </h2>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/contact#lead-form" data-testid="final-band-quote" className="btn-pill bg-[#faf6ef] text-[#1e2a5a] hover:bg-[#1e2a5a] hover:text-[#faf6ef] shadow-cta py-4 px-8 text-sm font-bold">
            Begin Your Journey <ArrowRight size={16} strokeWidth={2.2} />
          </Link>
          <a href={WHATSAPP_LINK()} target="_blank" rel="noreferrer" data-testid="final-band-whatsapp"
             onClick={() => window.gtag?.("event", "whatsapp_click", { location: "final_band" })}
             className="btn-pill bg-[#25D366] hover:bg-[#20c05c] text-white py-4 px-8 text-sm font-bold">
            <WhatsAppIcon size={16} /> Talk to a specialist
          </a>
        </div>
      </div>
    </section>
  );
}

/* ============== HOME PAGE — luxury repositioned order ============== */
export default function Home() {
  return (
    <>
    <Helmet>
      <title>Indus Art | Luxury Hand Block Printed Textiles from Jaipur</title>
      <meta name="description" content="Luxury handmade textiles — hand block-printed in Jaipur since 2007. Curated collections of cotton and linen for homes and brands that value provenance." />
      <meta property="og:title" content="Indus Art | Luxury Hand Block Printed Textiles from Jaipur" />
      <meta property="og:description" content="Luxury handmade textiles — hand block-printed in Jaipur since 2007. Curated collections across 11 categories. Trusted by buyers in 30+ countries." />
      <meta property="og:url" content="https://www.indusartjaipur.com/" />
      <meta property="og:image" content="https://www.indusartjaipur.com/og-image.jpg" />
      <link rel="canonical" href="https://www.indusartjaipur.com/" />
    </Helmet>
    <main data-testid="home-page">
      <Hero />
      <ProductsCatalog />
      <TrustRotator />
      <AutoPlayVideo />
      <CustomerLove />
      <WhyIndusArt />
      <CraftStory />
      <OrderProcessSection />
      <QuoteFormSection />
      <GlobalPresence />
      <TestimonialsSection />
      <GoogleReviewsSection />
      <ManufacturingStepper />
      <OnFilmSection />
      <FAQSection />
      <FinalBand />
    </main>
    </>
  );
}
